import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Pencil, Code2, X, Plus, Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SuggestedSkill {
  id: string;
  name: string;
}

interface SkillsSectionProps {
  skills: string[];
  suggestedSkills: SuggestedSkill[];
  isLoadingSkills: boolean;
  isEditing: boolean;
  isSaving: boolean;
  onEditClick: () => void;
  onCancel: () => void;
  onSave: () => void;
  onAddSkill: (skill: string) => void;
  onRemoveSkill: (skill: string) => void;
  onResetSkills?: () => void;
}

export function SkillsSection({
  skills,
  suggestedSkills,
  isLoadingSkills,
  isEditing,
  isSaving,
  onEditClick,
  onSave,
  onAddSkill,
  onRemoveSkill,
  onResetSkills,
}: SkillsSectionProps) {
  const hasSkills = skills.length > 0;
  const showEditing = isEditing || !hasSkills;

  return (
    <div className={cn(
      "rounded-2xl sm:rounded-3xl lg:rounded-[40px] p-5 sm:p-6 lg:p-8",
      !hasSkills
        ? "bg-amber-50/50 border-2 border-dashed border-amber-500/30"
        : "bg-white border border-slate-200"
    )}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <Code2 className="h-5 w-5 sm:h-6 sm:w-6 text-slate-700" />
          <h3 className="text-xl sm:text-2xl text-slate-900">Skills & Expertise</h3>
          {!hasSkills && (
            <Badge className="bg-amber-500 text-white rounded-[48px] px-3 sm:px-4 py-1 text-xs">
              Incomplete
            </Badge>
          )}
        </div>
        {hasSkills && !isEditing && (
          <button
            onClick={onEditClick}
            className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3.5 rounded-[48px] border-2 border-slate-200 bg-white text-slate-900 font-normal text-sm sm:text-base hover:bg-slate-50 transition-colors"
          >
            <Pencil className="w-4 h-4 sm:w-5 sm:h-5" />
            Edit
          </button>
        )}
      </div>

      {showEditing ? (
        <div className="space-y-6">
          {/* Selected Skills */}
          <div>
            <Label className="text-sm font-bold text-slate-700 mb-3 block">
              Your Skills ({skills.length}/10)
            </Label>
            <div className="flex flex-wrap gap-2 min-h-[40px]">
              {skills.length > 0 ? skills.map((skill) => (
                <Badge
                  key={skill}
                  className="bg-[#d1e0fc] text-[#2b42a3] rounded-[48px] px-3 py-1.5 text-xs font-semibold flex items-center gap-1"
                >
                  {skill}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onRemoveSkill(skill);
                    }}
                    className="ml-1 hover:text-red-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )) : (
                <span className="text-slate-400 text-sm">No skills selected yet</span>
              )}
            </div>
          </div>

          {/* Suggested Skills */}
          <div>
            <Label className="text-sm font-bold text-slate-700 mb-3 block">Suggested Skills</Label>
            {isLoadingSkills ? (
              <div className="flex flex-wrap gap-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-8 w-24 rounded-full" />
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {suggestedSkills
                  .filter(skill => !skills.includes(skill.name))
                  .slice(0, 15)
                  .map((skill) => (
                    <Badge
                      key={skill.id}
                      variant="outline"
                      className="cursor-pointer hover:bg-[#4669ed]/10 hover:border-[#4669ed] rounded-full px-3 py-1.5 transition-all border-slate-200"
                      onClick={() => onAddSkill(skill.name)}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      {skill.name}
                    </Badge>
                  ))}
              </div>
            )}
          </div>

          {/* Save/Cancel buttons */}
          <div className="flex gap-2">
            {isEditing && onResetSkills && (
              <Button
                variant="outline"
                onClick={onResetSkills}
                disabled={isSaving}
                className="rounded-[48px]"
              >
                Cancel
              </Button>
            )}
            {hasSkills && (
              <Button 
                onClick={onSave} 
                disabled={isSaving}
                className="bg-[#4669ed] hover:bg-[#2b42a3] rounded-[48px] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="h-5 w-5 mr-2" />
                    Save Skills
                  </>
                )}
              </Button>
            )}
          </div>

          {!hasSkills && !isEditing && (
            <div className="text-center py-4">
              <p className="text-slate-500 text-sm max-w-[400px] mx-auto">
                Help clients find you by adding your AI/ML skills, frameworks, and areas of expertise.
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {skills.map((skill) => (
            <Badge
              key={skill}
              className="bg-[#d1e0fc] text-[#2b42a3] rounded-[48px] px-3 py-1.5 text-xs font-semibold"
            >
              {skill}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
